import type { Order, OrderItem, CustomerType, DeliveryType, ItemPrice, OrderPricing } from '../types/order';
import type { Deal, DeliveryZone, DeliveryRange, PricingState } from '../store/slices/pricingSlice';

/**
 * Calculates pricing for order items based on customer type
 *
 * For Individual customers:
 * - Calculates total order quantity (sum of all items)
 * - Finds the largest applicable deal that doesn't exceed total quantity
 * - Applies the deal as many times as possible (floor of totalQuantity / dealQuantity)
 * - Charges remainder at regular pricePerDozen
 * - Calculates effective pricePerDozen as totalPrice / totalQuantity
 * - Falls back to base pricePerDozen if no deal applies
 *
 * For Retailer customers:
 * - Calculates total order quantity (sum of all items)
 * - Applies tiered pricing: < 50 units or >= 50 units
 * - All items in order get same price per dozen
 * - Deals do NOT apply to retailers
 *
 * @param items - Array of order items
 * @param customerType - 'Individual' or 'Retailer'
 * @param pricingState - Pricing configuration subset
 * @returns Array of ItemPrice with dealId if applicable
 */
export function calculateItemPricing(
  items: OrderItem[],
  customerType: CustomerType,
  pricingState: {
    pricePerDozen: number;
    retailerPriceUnder50: number;
    retailerPrice50Plus: number;
    deals: Deal[];
  }
): ItemPrice[] {
  // Calculate total order quantity (sum across all items)
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

  if (customerType === 'Individual') {
    // Get current date for date range validation
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0); // Set to start of day for consistent comparison

    // Find the largest deal that can be applied (deals are sorted ascending by quantity in pricingSlice)
    // We want the biggest deal that doesn't exceed the total quantity AND is within the date range
    const applicableDeal = pricingState.deals
      .filter(deal => {
        // Check quantity
        if (deal.quantity > totalQuantity) return false;

        // Check date range
        const startDate = new Date(deal.startDate);
        const endDate = new Date(deal.endDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);

        return currentDate >= startDate && currentDate <= endDate;
      })
      .sort((a, b) => b.quantity - a.quantity)[0]; // Get largest applicable deal

    if (applicableDeal) {
      // Calculate how many times to apply the deal and the remainder
      const dealApplications = Math.floor(totalQuantity / applicableDeal.quantity);
      const remainder = totalQuantity % applicableDeal.quantity;

      // Calculate total price: (deal price × applications) + (remainder × base price)
      const dealTotal = dealApplications * applicableDeal.price;
      const remainderTotal = remainder * pricingState.pricePerDozen;
      const totalPrice = dealTotal + remainderTotal;

      // Calculate effective price per dozen
      const effectivePricePerDozen = totalPrice / totalQuantity;

      console.log(
        `[Pricing] Individual order: ${totalQuantity} dozen → Applied "${applicableDeal.quantity} for $${applicableDeal.price}" deal ${dealApplications}x (${dealApplications * applicableDeal.quantity} dozen @ $${applicableDeal.price}/${applicableDeal.quantity} = $${dealTotal.toFixed(2)})${remainder > 0 ? ` + ${remainder} dozen @ $${pricingState.pricePerDozen} = $${remainderTotal.toFixed(2)}` : ''} → Total: $${totalPrice.toFixed(2)} (effective $${effectivePricePerDozen.toFixed(2)}/dozen)`
      );

      return items.map(item => ({
        recipeId: item.recipeId,
        quantity: item.quantity,
        pricePerDozen: effectivePricePerDozen,
        dealId: applicableDeal.id
      }));
    } else {
      // Fallback to base price
      console.log(
        `[Pricing] Individual order: ${totalQuantity} dozen → using base price $${pricingState.pricePerDozen}/dozen`
      );
      return items.map(item => ({
        recipeId: item.recipeId,
        quantity: item.quantity,
        pricePerDozen: pricingState.pricePerDozen
      }));
    }
  } else {
    // Retailer customer: use tiered pricing
    const pricePerDozen = totalQuantity >= 50 ? pricingState.retailerPrice50Plus : pricingState.retailerPriceUnder50;

    console.log(
      `[Pricing] Retailer order: ${totalQuantity} dozen → $${pricePerDozen}/dozen (${totalQuantity >= 50 ? '≥50 tier' : '<50 tier'})`
    );

    // Apply same price to all items (no dealId for retailers)
    return items.map(item => ({
      recipeId: item.recipeId,
      quantity: item.quantity,
      pricePerDozen
    }));
  }
}

/**
 * Calculates delivery fee based on delivery type, city zones, and distance ranges
 *
 * Priority order:
 * 1. Pickup orders: $0 (immediate return)
 * 2. City match in deliveryZones (case-insensitive, trimmed)
 * 3. Distance match in deliveryRanges (manual distance takes priority over auto distance)
 * 4. Fallback: $0 with console warning
 *
 * @param deliveryType - 'Pickup' or 'Delivery'
 * @param city - Delivery city from order
 * @param distanceFromHome - Optional auto-calculated distance in km from geocoding
 * @param manualDistance - Optional user-entered distance in km (takes priority)
 * @param pricingState - Delivery zones and ranges subset
 * @returns Object with { deliveryFee, deliveryZoneId?, deliveryRangeId? }
 */
export function calculateDeliveryFee(
  deliveryType: DeliveryType,
  city: string,
  distanceFromHome: number | undefined,
  manualDistance: number | undefined,
  pricingState: {
    deliveryZones: DeliveryZone[];
    deliveryRanges: DeliveryRange[];
  }
): {
  deliveryFee: number;
  deliveryZoneId?: string;
  deliveryRangeId?: string;
} {
  // 1. Pickup orders always $0
  if (deliveryType === 'Pickup') {
    return { deliveryFee: 0 };
  }

  // 2. Check city zones (case-insensitive, trimmed)
  const normalizedCity = city.trim().toLowerCase();
  const matchedZone = pricingState.deliveryZones.find(zone =>
    zone.towns.some(town => town.trim().toLowerCase() === normalizedCity)
  );

  if (matchedZone) {
    console.log(
      `[Pricing] Matched delivery zone: ${matchedZone.id} for city "${city}" → $${matchedZone.deliveryPrice}`
    );
    return {
      deliveryFee: matchedZone.deliveryPrice,
      deliveryZoneId: matchedZone.id
    };
  }

  // 3. Check distance ranges (prioritize manual distance over auto distance)
  const effectiveDistance = manualDistance ?? distanceFromHome;

  if (effectiveDistance !== undefined) {
    // Ranges are sorted by minDistance in pricingSlice
    const matchedRange = pricingState.deliveryRanges.find(
      range => effectiveDistance >= range.minDistance && effectiveDistance <= range.maxDistance
    );

    if (matchedRange) {
      const distanceSource = manualDistance !== undefined ? 'manual' : 'geocoded';
      console.log(
        `[Pricing] Matched delivery range: ${matchedRange.id} for ${distanceSource} distance ${effectiveDistance} km → $${matchedRange.deliveryPrice}`
      );
      return {
        deliveryFee: matchedRange.deliveryPrice,
        deliveryRangeId: matchedRange.id
      };
    }
  }

  // 4. Fallback: $0 with warning
  console.warn(
    `[Pricing] No delivery fee match for city "${city}"` +
      (effectiveDistance !== undefined
        ? ` (distance: ${effectiveDistance} km)`
        : ' (distance unavailable - geocoding pending)')
  );

  return { deliveryFee: 0 };
}

/**
 * Calculates order totals from item prices and delivery fee
 *
 * @param itemPrices - Array of ItemPrice with quantity and pricePerDozen
 * @param deliveryFee - Calculated delivery fee
 * @returns Object with { subtotal, total }
 */
export function calculateOrderTotals(
  itemPrices: ItemPrice[],
  deliveryFee: number
): {
  subtotal: number;
  total: number;
} {
  const subtotal = itemPrices.reduce((sum, item) => sum + item.quantity * item.pricePerDozen, 0);

  const total = subtotal + deliveryFee;

  return { subtotal, total };
}

/**
 * Master function to calculate complete pricing for an order
 *
 * Orchestrates all pricing calculations and returns complete OrderPricing object
 *
 * @param order - Order to calculate pricing for
 * @param pricingState - Complete pricing state from Redux
 * @returns Complete OrderPricing object
 */
export function calculateOrderPricing(order: Order, pricingState: PricingState): OrderPricing {
  // 1. Calculate item pricing
  const itemPrices = calculateItemPricing(order.items, order.customerType, pricingState);

  // 2. Calculate delivery fee (manual distance takes priority)
  const { deliveryFee, deliveryZoneId, deliveryRangeId } = calculateDeliveryFee(
    order.deliveryType,
    order.city,
    order.distanceFromHome,
    order.manualDistance,
    pricingState
  );

  // 3. Calculate totals
  const { subtotal, total } = calculateOrderTotals(itemPrices, deliveryFee);

  // 4. Return complete pricing
  return {
    itemPrices,
    subtotal,
    deliveryFee,
    deliveryZoneId,
    deliveryRangeId,
    total,
    calculatedAt: new Date().toISOString()
  };
}
